import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import {
  IDeleteParams,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IJumble } from './jumble.interface';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl2-jumbles';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withEntities<IJumble, '_id'>({ idKey: '_id' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class JumbleCoreService {
  private _loading = false;

  /**
   * @name jumblesList$
   * @description emits stored list of the jumbles
   */

  jumblesList$ = store.pipe(selectAllEntities());
  storeState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.storeState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getJumbles(params?: IListParams): void {
    this._loading = true;
    this._api
      .get('jumbles/list', params || {})
      .subscribe((data: IJumblesListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addJumble(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('jumbles/add', dataInput || {}).pipe(
      tap((data: IJumble) => {
        store.update(addEntities([data]));
        this._loading = false;
      }),
    );
  }

  public editJumble(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('jumbles/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IJumble) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteJumble(data: IDeleteParams): Observable<any> {
    return this._api.delete('jumbles', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
      }),
    );
  }

  public getJumbleById(jumbleId: string): Observable<IJumble> {
    return this._api.get(`jumbles/${jumbleId}`);
  }

  public getJumbleInfo(id: string): IJumble {
    const jumble = store.query(getEntity(id));
    return jumble;
  }

  public resolveBackgroundId(jumble: string | IJumble): string {
    let jumbleId: string | IJumble = '';
    if (jumble) {
      jumbleId = jumble;
    }
    if (jumble['_id']) {
      jumbleId = jumble['_id'];
    }
    return jumbleId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getJumbles();
    }
  }
}

interface IJumblesListResponse {
  list: IJumble[];
  totalCount: number;
}
